/**
 * v2.51 — Anticipos page.
 *
 * Allows owner/accounting users to record anticipos (customer advance payments)
 * and reverse them within the current session. Gated on:
 *   - user.role === 'owner' || user.role === 'accounting'
 *   - user.features?.writes_enabled === true
 *
 * The session list is client-local (no GET-list endpoint required).
 */
import { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Badge, Button, Card, Select, SelectItem,
  Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow,
  Text, Title,
} from '@tremor/react';
import { currentUser } from '../api/auth';
import { fetchCashCodes } from '../api/payments';
import { getStores } from '../api/priceUpload';
import { recordAnticipo, reverseAnticipo } from '../api/anticipos';
import AccessDenied from '../components/AccessDenied';
import Header from '../components/Header';
import { t } from '../lib/i18n';
import { licenseAllowsWrites } from '../lib/roles';
import type { Anticipo, CashCode, Store, User } from '../api/types';
import DrillCell from '../components/DrillCell';

const fmtMxn = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN',
  minimumFractionDigits: 2,
});

// --------------------------------------------------------------------------

export default function Anticipos() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    currentUser()
      .then((u) => { if (alive) setUser(u); })
      .catch(() => { if (alive) setUser(null); });
    return () => { alive = false; };
  }, []);

  if (user === undefined) return null;
  return user ? <AnticiposInner user={user} /> : <Navigate to="/login" replace />;
}

// --------------------------------------------------------------------------

function AnticiposInner({ user }: { user: User }) {
  const isAuthorized =
    (user.role === 'owner' || user.role === 'accounting') &&
    !!user.features?.writes_enabled &&
    licenseAllowsWrites(user);

  // Role qualifies, but writes are blocked either by the writes_enabled
  // feature flag or by the license (expired/cannot_validate/misconfigured/
  // clock_skew) — either way the form stays hidden and this message shows.
  const writesDisabled =
    (user.role === 'owner' || user.role === 'accounting') &&
    (!user.features?.writes_enabled || !licenseAllowsWrites(user));

  // Cash codes for the método dropdown
  const [cashCodes, setCashCodes] = useState<CashCode[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [sid, setSid] = useState(0);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  // Form state
  const [anum, setAnum] = useState('');
  const [amount, setAmount] = useState('');
  const [cashCode, setCashCode] = useState('');
  const [reference, setReference] = useState('');
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Session-local list of recorded anticipos
  const [sessionList, setSessionList] = useState<Anticipo[]>([]);
  // Tracks trankeys being reversed (for button disabled state)
  const [reversing, setReversing] = useState<Set<number>>(new Set());
  // Tracks trankeys already reversed (mark row)
  const [reversed, setReversed] = useState<Set<number>>(new Set());

  // Prevent stale async updates after unmount
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  // Load stores on mount — owner must pick the account's store for the write.
  useEffect(() => {
    if (!isAuthorized) return;
    let cancelled = false;
    getStores()
      .then((s) => {
        if (cancelled) return;
        setStores(s);
        if (s.length && sid === 0) setSid(s[0].sid);
      })
      .catch((e: Error) => { if (!cancelled) setLoadErr(e.message ?? String(e)); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthorized]);

  // (Re)load cash codes whenever the selected store changes.
  useEffect(() => {
    if (!isAuthorized || sid === 0) return;
    let cancelled = false;
    setLoadErr(null);
    fetchCashCodes(sid)
      .then((codes) => { if (!cancelled) setCashCodes(codes); })
      .catch((e: Error) => { if (!cancelled) setLoadErr(e.message ?? String(e)); });
    return () => { cancelled = true; };
  }, [isAuthorized, sid]);

  const anumNum = parseInt(anum, 10);
  const amountNum = parseFloat(amount) || 0;
  const canSubmit =
    !submitting &&
    sid > 0 &&
    Number.isInteger(anumNum) &&
    anumNum > 0 &&
    amountNum > 0 &&
    cashCode !== '';

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitErr(null);
    setSubmitting(true);
    try {
      const res = await recordAnticipo({
        sid,
        anum: anumNum,
        amount: amountNum,
        cash_code: cashCode,
        reference,
      });
      if (!alive.current) return;
      // Prepend to session list
      const newAnticipo: Anticipo = {
        tnum: res.data.tnum,
        trankey: res.data.trankey,
        anum: anumNum,
        amount: amountNum,
        cash_code: cashCode,
        reference,
      };
      setSessionList((prev) => [newAnticipo, ...prev]);
      // Clear form
      setAnum('');
      setAmount('');
      setCashCode('');
      setReference('');
    } catch (e) {
      if (alive.current) {
        setSubmitErr((e as Error).message ?? t.anticipos.errors.generic);
      }
    } finally {
      if (alive.current) setSubmitting(false);
    }
  }

  async function handleReverse(trankey: number) {
    if (!window.confirm(t.anticipos.reverseConfirm)) return;
    setReversing((prev) => new Set(prev).add(trankey));
    try {
      await reverseAnticipo(sid, trankey);
      if (!alive.current) return;
      setReversed((prev) => new Set(prev).add(trankey));
    } catch {
      // Silently recover — the button re-enables and the row stays
    } finally {
      if (alive.current) {
        setReversing((prev) => {
          const next = new Set(prev);
          next.delete(trankey);
          return next;
        });
      }
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen flex flex-col">
      <Header user={user} />

      <main className="flex-1 px-6 py-6 space-y-4 max-w-screen-xl mx-auto w-full">
        <div>
          <Title>{t.anticipos.title}</Title>
          <Text className="text-tremor-content text-sm">{t.anticipos.subtitle}</Text>
        </div>

        {/* Writes disabled for this role-combo (role OK but feature off) */}
        {writesDisabled && (
          <Card>
            <Text className="text-amber-600 dark:text-amber-400">{t.anticipos.disabled}</Text>
          </Card>
        )}

        {/* Role not authorized at all */}
        {!isAuthorized && !writesDisabled && (
          <AccessDenied user={user} />
        )}

        {isAuthorized && (
          <>
            {/* ── Record form ─────────────────────────────────────────── */}
            <Card>
              <Title className="text-base">{t.anticipos.form.submit}</Title>

              {loadErr && (
                <Text className="text-red-600 dark:text-red-400 text-sm mt-2">{loadErr}</Text>
              )}

              <form onSubmit={handleSubmit} className="mt-4 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Tienda (store) — the account's store; the daemon needs sid */}
                  <label className="text-sm">
                    <div className="text-tremor-content text-xs mb-1">Tienda</div>
                    <Select
                      value={sid ? String(sid) : ''}
                      onValueChange={(v) => setSid(parseInt(v, 10) || 0)}
                      enableClear={false}
                    >
                      {stores.map((s) => (
                        <SelectItem key={s.sid} value={String(s.sid)}>
                          {s.sid} — {s.name}
                        </SelectItem>
                      ))}
                    </Select>
                  </label>

                  {/* Cliente (anum) */}
                  <label className="text-sm">
                    <div className="text-tremor-content text-xs mb-1">{t.anticipos.form.customer}</div>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={anum}
                      onChange={(e) => setAnum(e.target.value)}
                      placeholder="Ej: 1042"
                      className="w-full rounded border border-tremor-border px-3 py-2"
                      required
                    />
                  </label>

                  {/* Monto */}
                  <label className="text-sm">
                    <div className="text-tremor-content text-xs mb-1">{t.anticipos.form.amount}</div>
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="0.00"
                      className="w-full rounded border border-tremor-border px-3 py-2"
                      required
                    />
                  </label>

                  {/* Método de pago */}
                  <label className="text-sm">
                    <div className="text-tremor-content text-xs mb-1">{t.anticipos.form.method}</div>
                    <Select
                      value={cashCode}
                      onValueChange={setCashCode}
                      placeholder={t.anticipos.form.methodPlaceholder}
                      enableClear={false}
                    >
                      {cashCodes.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.code} · {c.desc}
                        </SelectItem>
                      ))}
                    </Select>
                  </label>

                  {/* Referencia */}
                  <label className="text-sm">
                    <div className="text-tremor-content text-xs mb-1">{t.anticipos.form.reference}</div>
                    <input
                      type="text"
                      value={reference}
                      onChange={(e) => setReference(e.target.value)}
                      placeholder="Cheque, transferencia…"
                      className="w-full rounded border border-tremor-border px-3 py-2"
                    />
                  </label>
                </div>

                {submitErr && (
                  <Text className="text-red-600 dark:text-red-400 text-sm">{submitErr}</Text>
                )}

                <Button
                  type="submit"
                  disabled={!canSubmit}
                  loading={submitting}
                >
                  {t.anticipos.form.submit}
                </Button>
              </form>
            </Card>

            {/* ── Session list ──────────────────────────────────────────── */}
            <Card>
              <Title className="text-base">{t.anticipos.title}</Title>

              {sessionList.length === 0 ? (
                <Text className="mt-3 text-tremor-content text-sm italic">{t.anticipos.empty}</Text>
              ) : (
                <div className="mt-3 overflow-x-auto">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell>{t.anticipos.cols.folio}</TableHeaderCell>
                        <TableHeaderCell>{t.anticipos.cols.customer}</TableHeaderCell>
                        <TableHeaderCell className="text-right">{t.anticipos.cols.amount}</TableHeaderCell>
                        <TableHeaderCell>{t.anticipos.cols.method}</TableHeaderCell>
                        <TableHeaderCell>{t.anticipos.cols.reference}</TableHeaderCell>
                        <TableHeaderCell>{t.anticipos.cols.actions}</TableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sessionList.map((a) => {
                        const isReversed = reversed.has(a.trankey);
                        const isReversing = reversing.has(a.trankey);
                        return (
                          <TableRow
                            key={a.trankey}
                            className={isReversed ? 'opacity-50' : undefined}
                          >
                            {/* No sid on an anticipo row, so the ticket cell
                                stays plain text — a drill needs the store to
                                identify the ticket (JCD-59). */}
                            <TableCell className="font-mono">{a.tnum}</TableCell>
                            <TableCell>
                              <DrillCell target={a.anum > 0 ? { kind: 'account', anum: a.anum } : null}>
                                {a.name ?? a.anum}
                              </DrillCell>
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {fmtMxn.format(a.amount)}
                            </TableCell>
                            <TableCell>{a.cash_code}</TableCell>
                            <TableCell>{a.reference || '—'}</TableCell>
                            <TableCell>
                              {isReversed ? (
                                <Badge color="gray">Reversado</Badge>
                              ) : (
                                <Button
                                  size="xs"
                                  variant="secondary"
                                  color="red"
                                  disabled={isReversing}
                                  loading={isReversing}
                                  onClick={() => handleReverse(a.trankey)}
                                >
                                  {t.anticipos.reverse}
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </Card>
          </>
        )}
      </main>
    </div>
  );
}
